import { formatMoney } from 'accounting'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck, faDownload } from '@fortawesome/pro-light-svg-icons'

import { Box, Button, Text } from '@domain-group/suit-theme'

import handleCallbackAction from '@functions/handleCallbackAction'

import useOrderReconciliation from '@hooks/useOrderReconciliation'
import useReporting from '@hooks/useReporting'

import type { CampaignModel } from '@models/types'

import TotalsListItem from './components/TotalsListItem'

type BalanceCalculationsProps = {
  campaign: CampaignModel,
  isReconcilable?: boolean,
  orderReconciliationPayload: ReturnType<typeof useOrderReconciliation>,
  selectedEntityType?: string,
}

const BalanceCalculations = (props: BalanceCalculationsProps) => {
  const {
    campaign, isReconcilable, orderReconciliationPayload, selectedEntityType,
  } = props

  const { id } = campaign

  const {
    balance,
    credits,
    callbacks: {
      bulkReconcile,
      setSelectedIds,
    },
    difference,
    inCredit,
    orderReconciliationItems: {
      totals: {
        actualTotal,
        creditNoteTotal,
        expectedTotal,
      },
    },
    selectedEntityIds,
  } = orderReconciliationPayload

  const useReportingPayload = useReporting({
    allowedReports: ['cost_of_sale'],
    defaultValues: { campaign_id: id },
    formDeps: [id],
  })

  const {
    callbacks: { generateReport: generateFn },
  } = useReportingPayload

  const generateReportPayload = {
    callbacks: {
      action: () => generateFn({ campaign }),
    },
    toastText: 'Report Queued',
  }

  return (
    <>
      <Box
        borderTop="1px solid"
        borderColor="lineColor"
        flexDirection="column"
        padding="large"
        paddingRight="xlarge"
      >
        <TotalsListItem label="Actual Total" value={actualTotal} />

        <TotalsListItem label="Credit Notes" value={creditNoteTotal} />

        <TotalsListItem label="Actual inc Credit Notes" value={actualTotal + creditNoteTotal} />

        <TotalsListItem label="Expected Total" value={expectedTotal} />

        <TotalsListItem label="Expected vs Actual Difference" value={difference} />

        <TotalsListItem label="Payments & Credits" value={credits} />

        <Box
          alignItems="center"
          borderTop="1px dashed"
          borderColor="lineColor"
          justifyContent="flex-end"
          paddingTop="large"
        >
          <Text color="bodyFontLightColor" fontSize="xsmall">
            Balance ({inCredit ? 'Overpaid' : 'Owing'})
          </Text>

          <Text color={inCredit ? 'green' : 'red'} fontSize="small" textAlign="right" width={100}>
            {formatMoney(balance)}
          </Text>
        </Box>
      </Box>

      <Box borderTop="1px solid" borderColor="lineColor" padding="large" justifyContent="flex-end">
        {isReconcilable && selectedEntityType && (
          <Button
            buttonStyle="primaryEdit"
            icon={<FontAwesomeIcon icon={faCircleCheck} />}
            onClick={() => {
              bulkReconcile({
                entityType: selectedEntityType,
                selectedEntityIds,
              })
            }}
            size="medium"
            // @ts-expect-error TS2322
            width={['100%', 'fit-content']}
            marginRight="small"
          >
            Reconcile
          </Button>
        )}

        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={faDownload} />}
          onClick={() => handleCallbackAction(generateReportPayload)}
          size="medium"
          // @ts-expect-error TS2322
          width={['100%', 'fit-content']}
        >
          Download
        </Button>
      </Box>
    </>
  )
}

export default BalanceCalculations
